import React from 'react';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_ORIGIN } from '../config/site';
import { toCanonicalPath } from '../utils/seo';
import { LOCALES, Locale, OG_LOCALE, useI18n } from '../i18n';

export interface SeoHeadProps {
  title: string;
  description: string;
  /** Route pathname or pages.json key (e.g. `about-us`, `/blog/foo`). */
  path: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
}

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  path,
  keywords,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noIndex = false,
}) => {
  const { locale } = useI18n();
  const canonical = buildLocalizedCanonicalUrl(path, locale);
  const robots = noIndex ? 'noindex, follow' : 'index, follow';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      {LOCALES.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={buildLocalizedCanonicalUrl(path, l)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildLocalizedCanonicalUrl(path, 'en')} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={OG_LOCALE[locale]} />
      {LOCALES.filter((l) => l !== locale).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

/** Absolute URL of `path` in `locale`, e.g. ('about-us', 'km') → https://akas.co/km/about-us. */
function buildLocalizedCanonicalUrl(path: string, locale: Locale): string {
  const suffix = toCanonicalPath(path) === '/' ? '' : toCanonicalPath(path);
  return `${SITE_ORIGIN}/${locale}${suffix}`;
}

export default SeoHead;
